/**
 * Idioma da interface. O cliente é o único lugar do sistema que sabe falar com
 * o jogador: o servidor manda código e argumento, nunca frase.
 *
 * Preferência no localStorage, como o áudio — não é segredo, é gosto.
 */
export type Idioma = 'pt-BR' | 'en-US' | 'es-ES';

export const IDIOMAS: readonly Idioma[] = ['pt-BR', 'en-US', 'es-ES'];

/** Cada um no próprio idioma: quem procura inglês não lê "Inglês" para achar. */
export const NOME_DO_IDIOMA: Readonly<Record<Idioma, string>> = {
  'pt-BR': 'Português',
  'en-US': 'English',
  'es-ES': 'Español'
};

const PADRAO: Idioma = 'pt-BR';
const CHAVE_STORAGE = 'uoidle.idioma';
const UM_ANO_S = 60 * 60 * 24 * 365;

type Plural = { readonly one: string; readonly other: string };
type Valor = string | Plural;
type Catalogo = Readonly<Record<string, Valor>>;

/**
 * Um `import()` por idioma, **estático por chave**: o Vite emite um chunk para
 * cada e só o escolhido é baixado. Nada de `import('./' + id)` — id vindo do
 * localStorage não vira caminho de arquivo.
 */
const CARREGADORES: Readonly<Record<Idioma, () => Promise<unknown>>> = {
  'pt-BR': () => import('./pt-BR.json'),
  'en-US': () => import('./en-US.json'),
  'es-ES': () => import('./es-ES.json')
};

let atual: Idioma = PADRAO;
let catalogo: Catalogo = {};
const ouvintes = new Set<() => void>();
const aoGravar = new Set<(id: Idioma) => void>();

const UMA_CASA: Intl.NumberFormatOptions = { minimumFractionDigits: 1, maximumFractionDigits: 1 };
let nf = new Intl.NumberFormat(PADRAO);
let nf1 = new Intl.NumberFormat(PADRAO, UMA_CASA);
let hf = new Intl.DateTimeFormat(PADRAO, { hour: '2-digit', minute: '2-digit' });
let rf = new Intl.RelativeTimeFormat(PADRAO, { numeric: 'auto' });
let pr = new Intl.PluralRules(PADRAO);

export function idiomaAtual(): Idioma {
  return atual;
}

export function ehIdioma(raw: unknown): raw is Idioma {
  return typeof raw === 'string' && (IDIOMAS as readonly string[]).includes(raw);
}

/** O que o jogador escolheu neste navegador. Vazio se ele ainda não escolheu. */
export function preferenciaGuardada(): Idioma | null {
  try {
    const guardado = localStorage.getItem(CHAVE_STORAGE);
    if (ehIdioma(guardado)) return guardado;
  } catch {
    /* modo privado ou storage cheio — tenta o cookie */
  }
  return lerCookie();
}

/** Guardado > navegador. Lista fechada: nada entra sem passar aqui. */
export function idiomaInicial(): Idioma {
  return preferenciaGuardada() ?? doNavegador(navigator.languages ?? [navigator.language]);
}

export function doNavegador(tags: readonly string[]): Idioma {
  for (const tag of tags) {
    const base = (tag || '').toLowerCase().split('-')[0];
    if (base === 'pt') return 'pt-BR';
    if (base === 'es') return 'es-ES';
    if (base === 'en') return 'en-US';
  }
  return 'en-US';
}

/**
 * Carrega o catálogo e só então troca. Enquanto o chunk baixa, a tela continua
 * no idioma anterior — nunca pisca com chave crua.
 */
/** Avisa quem precisa gravar a escolha fora do navegador (a conta, por exemplo). */
export function aoGravarIdioma(fn: (id: Idioma) => void): () => void {
  aoGravar.add(fn);
  return () => { aoGravar.delete(fn); };
}

export async function trocarIdioma(id: Idioma): Promise<void> {
  if (!ehIdioma(id)) return;
  // Antes do download do catálogo: um clique em Entrar no meio do caminho não perde a escolha.
  gravarPreferencia(id);
  const proximo = await carregar(id);
  if (proximo === null) return;
  const mudou = atual !== id;
  atual = id;
  catalogo = proximo;
  formatadores(id);
  document.documentElement.lang = id;
  for (const fn of ouvintes) fn();
  if (mudou) for (const fn of aoGravar) fn(id);
}

/** Chamado uma vez em main.tsx, antes do primeiro render. */
export async function iniciarIdioma(): Promise<void> {
  const escolhido = idiomaInicial();
  const carga = (await carregar(escolhido)) ?? (await carregar(PADRAO));
  atual = carga === null ? PADRAO : escolhido;
  catalogo = carga ?? {};
  formatadores(atual);
  document.documentElement.lang = atual;
  ouvirOutraAba();
}

export function assinarIdioma(fn: () => void): () => void {
  ouvintes.add(fn);
  return () => {
    ouvintes.delete(fn);
  };
}

/**
 * Função de módulo, não hook: o motor do canvas não é React e precisa dela.
 * Chave sem tradução volta como a própria chave — nunca `undefined` na tela.
 */
export function t(chave: string, args?: Record<string, string | number>): string {
  const bruto = catalogo[chave];
  if (bruto === undefined) {
    if (import.meta.env.DEV) console.warn(`[i18n] ${atual}: falta a chave "${chave}"`);
    return chave;
  }
  const texto = typeof bruto === 'string' ? bruto : plural(bruto, args);
  return interpolar(texto, args);
}

/** Existe tradução para esta chave? Onde há texto do servidor como reserva. */
export function existe(chave: string): boolean {
  return catalogo[chave] !== undefined;
}

export function numero(n: number): string {
  return nf.format(n);
}

/** "62,4" / "62.4"; "1,0 s", nunca "1 s". */
export function umaCasa(n: number): string {
  return nf1.format(n);
}

export function hora(ms: number): string {
  return hf.format(new Date(ms));
}

/** "há 2 horas" / "2 hours ago" / "hace 2 horas", de graça, do navegador. */
export function relativo(quando: string | number | Date): string {
  const alvo = quando instanceof Date ? quando : new Date(quando);
  if (Number.isNaN(alvo.getTime())) return '';
  const segundos = Math.round((alvo.getTime() - Date.now()) / 1000);
  const escala: readonly (readonly [Intl.RelativeTimeFormatUnit, number])[] = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 7],
    ['week', 4.35],
    ['month', 12],
    ['year', Number.POSITIVE_INFINITY]
  ];
  let valor = segundos;
  for (const [unidade, passo] of escala) {
    if (Math.abs(valor) < passo) return rf.format(Math.round(valor), unidade);
    valor /= passo;
  }
  return rf.format(Math.round(valor), 'year');
}

// ---------------------------------------------------------------- internos

function gravarPreferencia(id: Idioma) {
  try {
    localStorage.setItem(CHAVE_STORAGE, id);
  } catch {
    /* cookie abaixo ainda pode valer na próxima visita */
  }
  try {
    if (typeof document === 'undefined') return;
    const host = typeof location !== 'undefined' ? location.hostname : '';
    const dominio = host === 'ultima-idle.com' || host.endsWith('.ultima-idle.com')
      ? '; Domain=.ultima-idle.com'
      : '';
    const seguro = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${CHAVE_STORAGE}=${encodeURIComponent(id)}; Path=/; Max-Age=${UM_ANO_S}; SameSite=Lax${dominio}${seguro}`;
  } catch {
    /* preferência só desta aba, então */
  }
}

function lerCookie(): Idioma | null {
  try {
    if (typeof document === 'undefined' || !document.cookie) return null;
    for (const parte of document.cookie.split(';')) {
      const eq = parte.indexOf('=');
      if (eq < 0) continue;
      if (parte.slice(0, eq).trim() !== CHAVE_STORAGE) continue;
      const valor = decodeURIComponent(parte.slice(eq + 1).trim());
      if (ehIdioma(valor)) return valor;
    }
  } catch {
    /* cookie ilegível */
  }
  return null;
}

let ouvindoAba = false;
function ouvirOutraAba() {
  if (ouvindoAba || typeof window === 'undefined') return;
  ouvindoAba = true;
  window.addEventListener('storage', ev => {
    if (ev.key !== CHAVE_STORAGE || ev.newValue === atual) return;
    if (ehIdioma(ev.newValue)) void trocarIdioma(ev.newValue);
  });
}

async function carregar(id: Idioma): Promise<Catalogo | null> {
  try {
    const mod = await CARREGADORES[id]();
    const bruto = (mod as { default?: unknown })?.default ?? mod;
    return normalizar(bruto);
  } catch {
    return null;
  }
}

/** O JSON é asset do repositório, mas custa pouco conferir o formato. */
function normalizar(bruto: unknown): Catalogo | null {
  if (typeof bruto !== 'object' || bruto === null) return null;
  const saida: Record<string, Valor> = {};
  for (const [chave, valor] of Object.entries(bruto as Record<string, unknown>)) {
    if (typeof valor === 'string') {
      saida[chave] = valor;
      continue;
    }
    if (typeof valor === 'object' && valor !== null) {
      const p = valor as Record<string, unknown>;
      if (typeof p.one === 'string' && typeof p.other === 'string') {
        saida[chave] = { one: p.one, other: p.other };
      }
    }
  }
  return saida;
}

function formatadores(id: Idioma) {
  // Construídos uma vez por troca: um Intl.NumberFormat por quadro do HUD
  // seria caro no laço de desenho.
  nf = new Intl.NumberFormat(id);
  nf1 = new Intl.NumberFormat(id, UMA_CASA);
  hf = new Intl.DateTimeFormat(id, { hour: '2-digit', minute: '2-digit' });
  rf = new Intl.RelativeTimeFormat(id, { numeric: 'auto' });
  pr = new Intl.PluralRules(id);
}

function plural(valor: Plural, args?: Record<string, string | number>): string {
  const n = typeof args?.n === 'number' ? args.n : Number(args?.n ?? 0);
  return pr.select(Number.isFinite(n) ? n : 0) === 'one' ? valor.one : valor.other;
}

function interpolar(texto: string, args?: Record<string, string | number>): string {
  if (!args) return texto;
  return texto.replace(/\{([a-zA-Z][a-zA-Z0-9]*)\}/g, (bruto, nome: string) => {
    const valor = args[nome];
    if (valor === undefined) return bruto;
    return typeof valor === 'number' ? nf.format(valor) : valor;
  });
}
